/**
 * Scientific calculator engine — wraps mathjs with DEG/RAD trig handling,
 * display formatting (NORM / FIX / SCI / ENG) and safe error reporting.
 *
 * No eval / Function — all parsing goes through mathjs.
 */

import { create, all, type MathJsInstance } from "mathjs";

export type AngleMode = "DEG" | "RAD";
export type DisplayMode = "NORM" | "FIX" | "SCI" | "ENG";

export interface CalcOptions {
  angleMode: AngleMode;
  displayMode: DisplayMode;
  fixDigits?: number; // for FIX/SCI/ENG
  ans?: number | null;
  memory?: number;
}

export interface CalcOk {
  ok: true;
  value: number;
  display: string;
}
export interface CalcErr {
  ok: false;
  error: "syntax" | "math" | "div_zero" | "range" | "paren";
  message: string;
}
export type CalcResult = CalcOk | CalcErr;

const math: MathJsInstance = create(all, {});

/** Convert deg <-> rad scalar. */
const toRad = (x: number) => (x * Math.PI) / 180;
const toDeg = (x: number) => (x * 180) / Math.PI;

/**
 * Replace UI symbols (×, ÷, π, √, ∛, x², x³, ^, %) with mathjs-compatible
 * text. The expression is otherwise plain JS-like math.
 */
export function normalizeExpression(input: string): string {
  let s = input;
  // Symbols → ascii
  s = s.replace(/×/g, "*").replace(/·/g, "*").replace(/÷/g, "/");
  s = s.replace(/−/g, "-").replace(/–/g, "-");
  s = s.replace(/π/g, "(pi)");
  // Roots
  s = s.replace(/∛\s*\(/g, "cbrt(");
  s = s.replace(/√\s*\(/g, "sqrt(");
  // Exponent superscripts
  s = s.replace(/²/g, "^2").replace(/³/g, "^3");
  // Factorial char ! is supported by mathjs natively.
  return s;
}

/**
 * Mismatched-paren check (cheap pre-check; mathjs would also fail).
 */
function parenBalance(s: string): number {
  let depth = 0;
  for (const c of s) {
    if (c === "(") depth++;
    else if (c === ")") depth--;
    if (depth < 0) return -1;
  }
  return depth;
}

/**
 * Build a mathjs scope with trig overrides for DEG mode plus Ans/M.
 */
function buildScope(opts: CalcOptions) {
  const deg = opts.angleMode === "DEG";
  const wrap1 = (fn: (x: number) => number, convertInput: boolean) =>
    (x: number) => fn(deg && convertInput ? toRad(x) : x);
  const wrapInv = (fn: (x: number) => number) =>
    (x: number) => (deg ? toDeg(fn(x)) : fn(x));

  return {
    Ans: opts.ans ?? 0,
    ans: opts.ans ?? 0,
    M: opts.memory ?? 0,
    sin: wrap1(Math.sin, true),
    cos: wrap1(Math.cos, true),
    tan: wrap1(Math.tan, true),
    asin: wrapInv(Math.asin),
    acos: wrapInv(Math.acos),
    atan: wrapInv(Math.atan),
    log: (x: number) => Math.log10(x), // base 10
    log10: (x: number) => Math.log10(x),
    ln: (x: number) => Math.log(x),
    exp: Math.exp,
  } as Record<string, unknown>;
}

/**
 * Format the result number per display mode.
 */
export function formatResult(value: number, opts: CalcOptions): string {
  if (!Number.isFinite(value)) {
    return value === Infinity ? "∞" : value === -Infinity ? "-∞" : "NaN";
  }
  const digits = Math.min(9, Math.max(0, opts.fixDigits ?? 6));

  if (opts.displayMode === "FIX") {
    return value.toFixed(digits);
  }
  if (opts.displayMode === "SCI") {
    return value.toExponential(digits).replace("e", " × 10^");
  }
  if (opts.displayMode === "ENG") {
    if (value === 0) return "0";
    const exp = Math.floor(Math.log10(Math.abs(value)));
    const eng = Math.floor(exp / 3) * 3;
    const mant = value / Math.pow(10, eng);
    return `${mant.toFixed(digits)} × 10^${eng}`;
  }
  // NORM: scientific only if very large/small
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e10 || abs < 1e-6)) {
    return value.toExponential(6).replace("e", " × 10^");
  }
  // Trim trailing zeros after up to 10 decimals
  const s = value.toPrecision(12);
  // Convert toPrecision exponential output back to fixed when reasonable
  if (s.includes("e")) return value.toExponential(6).replace("e", " × 10^");
  return parseFloat(s).toString();
}

/**
 * Evaluate an expression string. Always returns a result object.
 */
export function evaluateExpression(input: string, opts: CalcOptions): CalcResult {
  const trimmed = input.trim();
  if (!trimmed) {
    return { ok: false, error: "syntax", message: "Empty expression" };
  }

  const balance = parenBalance(trimmed);
  if (balance !== 0) {
    return { ok: false, error: "paren", message: "Syntax Error: Check parentheses" };
  }

  const expr = normalizeExpression(trimmed);
  const scope = buildScope(opts);

  let value: unknown;
  try {
    value = math.evaluate(expr, scope);
  } catch (e) {
    const msg = (e as Error)?.message ?? "";
    if (/Division by zero|Divide by zero/i.test(msg)) {
      return { ok: false, error: "div_zero", message: "Math Error: Cannot divide by zero" };
    }
    return { ok: false, error: "syntax", message: "Syntax Error" };
  }

  const num = typeof value === "number" ? value : Number(value);
  if (typeof value !== "number" || !Number.isFinite(num)) {
    if (num === Infinity || num === -Infinity) {
      return { ok: false, error: "div_zero", message: "Math Error: Cannot divide by zero" };
    }
    if (Number.isNaN(num)) {
      return { ok: false, error: "math", message: "Math Error" };
    }
    // mathjs may return BigNumber/Complex — coerce or fail
    if (Number.isFinite(num)) {
      return { ok: true, value: num, display: formatResult(num, opts) };
    }
    return { ok: false, error: "math", message: "Math Error" };
  }

  if (Math.abs(num) > 1e308) {
    return { ok: false, error: "range", message: "Range Error" };
  }

  return { ok: true, value: num, display: formatResult(num, opts) };
}
